import React from "react";

const UserTable = ({
  users,
  onSelectUser,
  currentPage,
  totalPages,
  onPageChange,
  onStatusToggle,
}) => {
  const getStatusStyle = (status) => {
    const isVisibleActive = status === true || status === "Active";
    if (isVisibleActive)
      return "bg-green-100 text-green-700 ring-1 ring-green-200";
    return "bg-amber-100 text-amber-700 ring-1 ring-amber-200";
  };

  const Pagination = () => (
    <div className="px-4 sm:px-8 py-4 sm:py-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shrink-0" />
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">
          Page {currentPage} of {totalPages || 1}
        </span>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-[#0B1C2D] hover:text-[#0B1C2D] disabled:opacity-20 disabled:cursor-not-allowed transition-all active:scale-95"
        >
          <span className="material-symbols-outlined text-[20px]">west</span>
        </button>

        <div className="hidden sm:flex gap-1">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => onPageChange(i + 1)}
              className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all ${currentPage === i + 1 ? "bg-[#0B1C2D] text-white shadow-lg" : "bg-white text-slate-400 hover:bg-slate-100"}`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-[#0B1C2D] hover:text-[#0B1C2D] disabled:opacity-20 disabled:cursor-not-allowed transition-all active:scale-95"
        >
          <span className="material-symbols-outlined text-[20px]">east</span>
        </button>
      </div>
    </div>
  );

  const UserActions = ({ user, isActive }) => (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => onSelectUser(user)}
        title="View Details"
        className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:bg-[#0B1C2D] hover:text-white transition-all duration-200 active:scale-90 shadow-sm"
      >
        <span className="material-symbols-outlined text-[18px]">visibility</span>
      </button>
      <button
        type="button"
        onClick={() => onStatusToggle && onStatusToggle(user.id, !isActive)}
        title={isActive ? "Deactivate User" : "Activate User"}
        className={`w-10 h-10 inline-flex items-center justify-center rounded-xl transition-all duration-200 active:scale-90 shadow-sm ${
          isActive
            ? "bg-amber-50 text-amber-500 hover:bg-amber-500 hover:text-white"
            : "bg-green-50 text-green-500 hover:bg-green-500 hover:text-white"
        }`}
      >
        <span className="material-symbols-outlined text-[18px]">
          {isActive ? "block" : "check_circle"}
        </span>
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl lg:rounded-lg border border-slate-200 overflow-hidden">
      {/* Mobile card list */}
      <div className="md:hidden divide-y divide-slate-100">
        {users.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">
            No users found
          </div>
        ) : (
          users.map((user) => {
            const isActive = user.status === true || user.status === "Active";
            const userRole = user.role || "User";
            const userCompany =
              user.companyName || user.company || "Independent";
            const userMobile = user.mobile || user.number || "N/A";

            return (
              <div
                key={user.id}
                role="button"
                tabIndex={0}
                className="p-4 active:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => onSelectUser(user)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectUser(user);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#0B1C2D] text-white flex items-center justify-center font-bold text-sm overflow-hidden shrink-0">
                    {user.profilePicURL ? (
                      <img
                        src={user.profilePicURL}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      (user.name || "U").charAt(0)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-black text-[#0B1C2D] truncate">
                          {user.name}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">
                          {user.email}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${getStatusStyle(user.status)}`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">
                          call
                        </span>
                        {userMobile}
                      </span>
                      <span className="flex items-center gap-1 truncate">
                        <span className="material-symbols-outlined text-[13px]">
                          domain
                        </span>
                        {userCompany}
                      </span>
                    </div>
                    <div
                      className="mt-3 flex items-center justify-between"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="inline-flex px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black uppercase">
                        {userRole}
                      </span>
                      <UserActions user={user} isActive={isActive} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-100">
              <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                User Details
              </th>
              <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                Contact & Company
              </th>
              <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                Role
              </th>
              <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                Status
              </th>
              <th className="px-8 py-5 text-right text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {users.map((user) => {
              const isActive = user.status === true || user.status === "Active";
              const userRole = user.role || "User";
              const userCompany =
                user.companyName || user.company || "Independent";
              const userMobile = user.mobile || user.number || "N/A";

              return (
                <tr
                  key={user.id}
                  onClick={() => onSelectUser(user)}
                  className="hover:bg-slate-50/50 transition-all duration-200 group cursor-pointer"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-2xl bg-[#0B1C2D] text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-slate-200 group-hover:scale-105 transition-transform overflow-hidden">
                        {user.profilePicURL ? (
                          <img
                            src={user.profilePicURL}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          (user.name || "U").charAt(0)
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-[#0B1C2D] tracking-tight">
                          {user.name}
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <span className="material-symbols-outlined text-[14px]">
                          call
                        </span>
                        <span className="text-xs font-bold">{userMobile}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <span className="material-symbols-outlined text-[14px]">
                          domain
                        </span>
                        <span className="text-[11px] font-medium">
                          {userCompany}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <div className="inline-flex px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-tight">
                      {userRole}
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${getStatusStyle(user.status)}`}
                    >
                      <span
                        className={`w-1 h-1 rounded-full ${isActive ? "bg-green-500" : "bg-amber-500"}`}
                      />
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-8 py-5 text-right">
                    <div className="flex items-center justify-end">
                      <UserActions user={user} isActive={isActive} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination />
    </div>
  );
};

export default UserTable;
